import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useSettingsStore from '../../store/useSettingsStore';

const routeLabels = {
  '': 'Dashboard',
  'dashboard': 'Dashboard',
  'dashboard-analytics': 'Dashboard Analytics',
  'dashboard-crypto': 'Dashboard Crypto',
  'dashboard-hrms': 'Dashboard HRMS',
  'dashboard-job': 'Dashboard Jobs',
  'login': 'Login',
  'register': 'Register',
  'forgot-password': 'Forgot Password',
  'reset-password': 'Reset Password',
  'two-factor': 'Two Factor Auth',
  'lock-screen': 'Lock Screen',
  '404': 'Page Not Found',
  '500': 'Server Error',
  '403': 'Forbidden',
  'users': 'Users',
  'analytics': 'Analytics',
  'products': 'Products',
  'orders': 'Orders',
  'messages': 'Chat',
  'calendar': 'Calendar',
  'database': 'Database',
  'settings': 'Settings',
  'ui-components': 'UI Components',
  'profile': 'Profile',
  'tables': 'Tables',
  'forms': 'Forms',
  'components': 'Components',
  'accordion': 'Accordion',
  'alert': 'Alert',
  'alert-dialog': 'Alert Dialog',
  'aspect-ratio': 'Aspect Ratio',
  'avatar': 'Avatar',
  'badge': 'Badge',
  'breadcrumb': 'Breadcrumb',
  'button': 'Button',
  'checkbox': 'Checkbox',
  'collapsible': 'Collapsible',
  'combobox': 'Combobox',
  'command': 'Command',
  'dialog': 'Dialog',
  'dropdown-menu': 'Dropdown Menu',
  'pagination': 'Pagination',
  'popover': 'Popover',
  'progress': 'Progress',
  'radio-group': 'Radio Group',
  'select': 'Select',
  'separator': 'Separator',
  'sheet': 'Sheet',
  'skeleton': 'Skeleton',
  'slider': 'Slider',
  'switch': 'Switch',
  'tabs': 'Tabs',
  'textarea': 'Textarea',
  'toggle': 'Toggle',
  'tooltip': 'Tooltip',
  'typography': 'Typography',
  'hrms': 'HRMS',
  'jobs': 'Job Portal',
  'starter': 'Starter Page',
};

const formatLabel = (segment) => {
  if (routeLabels[segment.toLowerCase()]) {
    return routeLabels[segment.toLowerCase()];
  }
  return segment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Converts a hex color (#rrggbb) to "H S% L%" string for CSS custom properties.
 */
function hexToHSL(hex) {
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  return `${h} ${s}% ${l}%`;
}

const ThemeManager = () => {
  const { theme, primaryColor, rtl, cardStyle } = useSettingsStore();
  const location = useLocation();

  useEffect(() => {
    const root = document.documentElement;

    // 1. Apply theme class
    root.classList.remove('light', 'dark', 'galaxy');
    let resolvedThemeMode = theme;
    if (theme === 'system') {
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      resolvedThemeMode = isDark ? 'dark' : 'light';
    }
    root.classList.add(resolvedThemeMode);

    // Sync store themeMode without triggering re-render if it is already identical
    if (useSettingsStore.getState().themeMode !== resolvedThemeMode) {
      useSettingsStore.getState().setThemeMode(resolvedThemeMode);
    }

    // 2. Apply RTL
    root.setAttribute('dir', rtl ? 'rtl' : 'ltr');

    // 3. Apply primary color
    const hsl = hexToHSL(primaryColor);
    root.style.setProperty('--primary', hsl);
    // Also update the sidebar primary
    root.style.setProperty('--sidebar-primary', hsl);
    // Update ring
    root.style.setProperty('--ring', hsl);

    // 4. Apply border radius
    const radiusMap = {
      none: '0px',
      sm: '0.25rem',
      md: '0.5rem',
      lg: '0.75rem',
      xl: '1rem',
      full: '9999px',
    };
    root.style.setProperty('--radius', radiusMap[cardStyle.rounded] ?? '0.5rem');

  }, [theme, primaryColor, rtl, cardStyle]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'system') return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      document.documentElement.classList.remove('light', 'dark', 'galaxy');
      const resolved = e.matches ? 'dark' : 'light';
      document.documentElement.classList.add(resolved);
      if (useSettingsStore.getState().themeMode !== resolved) {
        useSettingsStore.getState().setThemeMode(resolved);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  // Sync document title on page change (SEO Breadcrumb structure)
  useEffect(() => {
    const segments = location.pathname
      .split('/')
      .filter(Boolean)
      .filter(s => s !== 'apps' && s !== 'components' && isNaN(s));

    const baseTitle = 'Shadex - React js Admin Template';

    if (segments.length === 0 || (segments.length === 1 && segments[0] === 'dashboard')) {
      document.title = `Dashboard | ${baseTitle}`;
    } else {
      const pageTitle = segments.map(s => formatLabel(s)).join(' ');
      document.title = `${pageTitle} | ${baseTitle}`;
    }
  }, [location.pathname]);

  return null;
};

export default ThemeManager;
